import { API_BASE_URL } from "../../config/apiConfig";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaTimesCircle,
} from "react-icons/fa";

import "../../styles/pages/professional/VerificationStatus.css";

const API_BASE =
  API_BASE_URL;

const REQUIRED_DOCUMENTS = [
  {
    key: "identity",
    name: "Identity Verification",
    documentTypeIds: [2, 6, 10, 11],
    optional: false,
  },
  {
    key: "resume",
    name: "Resume",
    documentTypeIds: [4],
    optional: false,
  },
  {
    key: "immunization",
    name: "Immunization Record",
    documentTypeIds: [7],
    optional: false,
  },
  {
    key: "vulnerable-sector-check",
    name: "Vulnerable Sector Check",
    documentTypeIds: [15],
    optional: false,
  },
];

const OPTIONAL_DOCUMENTS = [
  {
    key: "police-check",
    name: "Police Check",
    documentTypeIds: [1],
    optional: true,
  },
  {
    key: "tb-test",
    name: "TB Test",
    documentTypeIds: [3],
    optional: true,
  },
  {
    key: "mask-fit",
    name: "Mask Fit",
    documentTypeIds: [9],
    optional: true,
  },
  {
    key: "n95-fit-test",
    name: "N95 Fit Test",
    documentTypeIds: [13],
    optional: true,
  },
];

const getToken = () =>
  localStorage.getItem(
    "zethAuthToken"
  ) ||
  sessionStorage.getItem(
    "zethAuthToken"
  );

const parseJsonResponse =
  async (response) => {
    const contentType =
      response.headers.get(
        "content-type"
      );

    if (
      !contentType?.includes(
        "application/json"
      )
    ) {
      throw new Error(
        "The server returned an invalid response."
      );
    }

    return response.json();
  };

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "_");

const isPastDate = (dateValue) => {
  if (!dateValue) {
    return false;
  }

  const date = new Date(dateValue);

  if (
    Number.isNaN(date.getTime())
  ) {
    return false;
  }

  return date.getTime() <
    new Date().setHours(
      0,
      0,
      0,
      0
    );
};

const getRecordStatus = (
  record,
  {
    missingStatus = "required",
    optional = false,
  } = {}
) => {
  if (!record) {
    return optional
      ? {
          label: "Optional",
          type: "optional",
        }
      : {
          label: "Not Uploaded",
          type: missingStatus,
        };
  }

  const expiryDate =
    record.expiry_date ||
    record.expiryDate;

  if (isPastDate(expiryDate)) {
    return {
      label: "Expired",
      type: "expired",
    };
  }

  const rawStatus =
    record.verification_status ||
    record.verificationStatus ||
    record.status;

  const status =
    normalizeStatus(rawStatus);

  if (
    [
      "verified",
      "approved",
      "active",
      "valid",
      "current",
    ].includes(status)
  ) {
    return {
      label: "Verified",
      type: "verified",
    };
  }

  if (
    [
      "rejected",
      "declined",
      "denied",
    ].includes(status)
  ) {
    return {
      label: "Rejected",
      type: "rejected",
    };
  }

  if (
    [
      "expired",
      "inactive",
      "invalid",
    ].includes(status)
  ) {
    return {
      label: "Expired",
      type: "expired",
    };
  }

  return {
    label: "Pending Review",
    type: "pending",
  };
};

const findDocument = (
  documents,
  documentTypeIds
) =>
  documents.find((document) =>
    documentTypeIds.includes(
      Number(
        document.document_type_id ??
          document.documentTypeId
      )
    )
  ) || null;


const getStatusIcon = (type) => {
  if (type === "verified") {
    return (
      <FaCheckCircle
        aria-hidden="true"
      />
    );
  }

  if (
    type === "rejected" ||
    type === "expired" ||
    type === "required"
  ) {
    return (
      <FaTimesCircle
        aria-hidden="true"
      />
    );
  }

  if (type === "optional") {
    return (
      <FaExclamationCircle
        aria-hidden="true"
      />
    );
  }

  return (
    <FaClock aria-hidden="true" />
  );
};

function VerificationStatus() {
  const [
    documents,
    setDocuments,
  ] = useState([]);

  const [
    licences,
    setLicences,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const loadVerificationData =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();

        if (!token) {
          throw new Error(
            "Authentication required. Please sign in again."
          );
        }

        const requestOptions = {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        };

        const [
          documentsResponse,
          licencesResponse,
        ] = await Promise.all([
          fetch(
            `${API_BASE}/professional/documents`,
            requestOptions
          ),

          fetch(
            `${API_BASE}/professionals/licenses`,
            requestOptions
          ),
        ]);

        const [
          documentsData,
          licencesData,
        ] = await Promise.all([
          parseJsonResponse(
            documentsResponse
          ),

          parseJsonResponse(
            licencesResponse
          ),
        ]);

        if (
          !documentsResponse.ok ||
          !documentsData.success
        ) {
          throw new Error(
            documentsData.message ||
              "Unable to load documents."
          );
        }

        if (!licencesResponse.ok) {
          throw new Error(
            licencesData.message ||
              "Unable to load licences."
          );
        }

        setDocuments(
          Array.isArray(
            documentsData.documents
          )
            ? documentsData.documents
            : []
        );

        setLicences(
          licencesData.licenses ||
            licencesData.licences ||
            licencesData.data ||
            []
        );
      } catch (loadError) {
        console.error(
          "Load verification status error:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load verification status."
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    document.title =
      "Verification Status | Zeth Health";

    loadVerificationData();
  }, [loadVerificationData]);

  const verificationItems =
    useMemo(() => {
      const documentItems = [
        ...REQUIRED_DOCUMENTS,
        ...OPTIONAL_DOCUMENTS,
      ].map((definition) => {
        const document =
          findDocument(
            documents,
            definition.documentTypeIds
          );

        return {
          key: definition.key,
          name: definition.name,
          record: document,
          ...getRecordStatus(
            document,
            {
              optional:
                definition.optional,
            }
          ),
        };
      });

    const licenceItems = licences.map(
  (licence, index) => {
    const profession =
      licence.profession ||
      licence.profession_name ||
      licence.professionName ||
      licence.licence_type ||
      licence.license_type ||
      "Professional";

    const province =
      licence.province ||
      licence.province_name ||
      licence.provinceName ||
      licence.jurisdiction ||
      "Province not specified";

    return {
      key:
        `professional-licence-${
          licence.id || index
        }`,

      name:
        `${profession} Licence — ${province}`,

      record: licence,

      ...getRecordStatus(
        licence,
        {
          optional: false,
          missingStatus: "required",
        }
      ),
    };
  }
);

  return [
  documentItems[0],
  documentItems[1],
  ...licenceItems,
  ...documentItems.slice(2),
];

    }, [documents, licences]);

  const requiredItems =
  verificationItems.filter(
    (item) =>
      item.key === "identity" ||
      item.key === "resume" ||
      item.key ===
        "immunization" ||
      item.key ===
        "vulnerable-sector-check" ||
      item.key.startsWith(
        "professional-licence-"
      )
  );

  const verifiedCount =
    requiredItems.filter(
      (item) =>
        item.type === "verified"
    ).length;

  const pendingCount =
    requiredItems.filter(
      (item) =>
        item.type === "pending"
    ).length;

  const rejectedCount =
    requiredItems.filter(
      (item) =>
        item.type === "rejected"
    ).length;

  const expiredCount =
    requiredItems.filter(
      (item) =>
        item.type === "expired"
    ).length;

  const missingCount =
    requiredItems.filter(
      (item) =>
        item.type === "required"
    ).length;

  const verificationPercentage =
    requiredItems.length > 0
      ? Math.round(
          (verifiedCount /
            requiredItems.length) *
            100
        )
      : 0;

  const overallStatus =
    useMemo(() => {
      if (
        requiredItems.length > 0 &&
        verifiedCount ===
          requiredItems.length
      ) {
        return {
          label: "Verified",
          type: "verified",
          message:
            "All required credentials and documents have been verified.",
        };
      }

      if (
        rejectedCount > 0
      ) {
        return {
          label: "Action Required",
          type: "rejected",
          message:
            "One or more credentials were rejected. Please review and replace the affected records.",
        };
      }

      if (expiredCount > 0) {
        return {
          label: "Action Required",
          type: "expired",
          message:
            "One or more required credentials have expired.",
        };
      }

      if (missingCount > 0) {
        return {
          label: "Incomplete",
          type: "required",
          message:
            "Upload all required credentials and documents to continue verification.",
        };
      }

      if (pendingCount > 0) {
        return {
          label: "Pending Review",
          type: "pending",
          message:
            "Your required credentials have been submitted and are awaiting review.",
        };
      }

      return {
        label: "Not Started",
        type: "required",
        message:
          "Add your required credentials to begin verification.",
      };
    }, [
      requiredItems.length,
      verifiedCount,
      rejectedCount,
      expiredCount,
      missingCount,
      pendingCount,
    ]);

  if (loading) {
    return (
      <section className="verification-page">
        <div className="verification-page-message">
          Loading verification status...
        </div>
      </section>
    );
  }

  return (
    <section className="verification-page">
      <div className="credential-child-description">
        <p>
          Review the verification status of
          your professional credentials and
          uploaded documents.
        </p>
      </div>

      {error && (
        <div
          className="verification-page-message verification-page-message-error"
          role="alert"
        >
          <span>{error}</span>

          <button
            type="button"
            onClick={
              loadVerificationData
            }
          >
            Try Again
          </button>
        </div>
      )}

      {!error && (
        <>
          <section className="verification-summary-card">
            <div>
              <h2>Overall Status</h2>

              <p>
                {overallStatus.message}
              </p>

              <div className="verification-progress-summary">
                <span>
                  {verifiedCount} of{" "}
                  {requiredItems.length}{" "}
                  required items verified
                </span>

                <strong>
                  {verificationPercentage}%
                </strong>
              </div>

              <div className="verification-progress-bar">
                <div
                  className="verification-progress-fill"
                  style={{
                    width:
                      `${verificationPercentage}%`,
                  }}
                />
              </div>
            </div>

            <span
              className={`verification-overall ${overallStatus.type}`}
            >
              {getStatusIcon(
                overallStatus.type
              )}

              <span>
                {overallStatus.label}
              </span>
            </span>
          </section>

          <section className="verification-list">
            {verificationItems.map(
              (item) => (
                <article
                  key={item.key}
                  className="verification-item"
                >
                  <div className="verification-item-details">
                    <strong>
                      {item.name}
                    </strong>

                    {item.record && (
  <small>
    {item.key.startsWith(
      "professional-licence-"
    )
      ? item.record
          .licence_number ||
        item.record
          .license_number ||
        "Licence number unavailable"
      : item.record
          .document_type_name ||
        item.record
          .original_name ||
        ""}
  </small>
)}
                  </div>

                  <span
                    className={`verification-badge ${item.type}`}
                  >
                    {getStatusIcon(
                      item.type
                    )}

                    <span>
                      {item.label}
                    </span>
                  </span>
                </article>
              )
            )}
          </section>
        </>
      )}
    </section>
  );
}

export default VerificationStatus;
